/*
 * ******************************************************
 * Copyright (C) 1994-2021 Babiel GmbH <info@babiel.com>
 *
 * This file is part of a Babiel development
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted without the express
 * permission of Babiel GmbH
 *
 * Any modification is not permitted without the express
 * permission of Babiel GmbH
 *
 * The file may contain confidential and or privileged materials.
 * Any review, retransmission, dissemination or other use of
 * or taking of any action in reliance upon, this file
 * by persons or entities other than the intended is prohibited.
 * ******************************************************
 */

import { Component } from './component';

export interface Registry {
  [componentName: string]: ((htmlElement: HTMLElement) => Component) | ((htmlElement: HTMLElement) => Promise<Component>);
}

export class ComponentSystem {
  instances: Component[] = [];

  constructor(private readonly registry: Registry) { }

  private static onDocReady(fn: () => void) {
    // see if DOM is already available
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      // call on next available tick
      setTimeout(fn, 1);
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  public init() {
    ComponentSystem.onDocReady(() => {
      this.lookupComponents().then(() => {
        this.setupComponents();
        this.setupEvents();
      });
    });
  }

  private async lookupComponents(root: Document | HTMLElement = document): Promise<void> {
    if (root instanceof HTMLElement && Object.prototype.hasOwnProperty.call(root.dataset, 'component')) {
      await this.initComponent(root);
    }

    const componentElements = root.querySelectorAll(`[data-component]`) as NodeListOf<HTMLElement>;

    if (componentElements.length) {
      for (const componentElement of componentElements) {
        await this.initComponent(componentElement);
      }
    }
  }

  private async initComponent(componentElement: HTMLElement): Promise<void> {
    const { component: componentName } = componentElement.dataset;

    if (!Object.prototype.hasOwnProperty.call(this.registry, componentName)) {
      return null;
    }

    const instance = await this.registry[componentName](componentElement);

    this.instances.push(instance);
  }

  private setupComponents(): void {
    this.instances.forEach((instance) => {
      if (typeof instance.init === 'function') {
        instance.init();
      }
    });
  }

  private setupEvents(): void {
    this.instances.forEach((instance) => {
      if (typeof instance.setupEvents === 'function') {
        instance.setupEvents();
      }
    });
  }
}
